import type { Customization } from '../types/customization';
import type { Dish } from '../types/dish';
import type { OptionAddon, Recipe } from '../types/recipe';

/** Điểm tối thiểu để tự ghép (0–1000). Cao hơn = an toàn hơn. */
export const RECIPE_AUTO_MATCH_MIN_SCORE = 720;
/** Nếu top1 − top2 < gap → coi là mơ hồ, không tự ghép. */
export const RECIPE_AUTO_MATCH_MIN_GAP = 40;

export interface MenuDishRef {
  code: string;
  name: string;
  group?: string;
}

export type RecipeMatchMethod =
  | 'code'
  | 'code_ci'
  | 'code_suffix'
  | 'name'
  | 'name_compact'
  | 'name_contains'
  | 'token'
  | 'fuzzy'
  | 'none';

export interface RecipeSyncChange {
  recipeId?: string;
  previousItemId: string;
  previousItemName: string;
  nextItemId: string;
  nextItemName: string;
  matchBy: RecipeMatchMethod;
  score: number;
  optionAddonsRemapped: number;
}

export interface UnmatchedRecipeRef {
  id?: string;
  itemId: string;
  itemName: string;
  group?: string;
  ingredientCount: number;
  /** Gợi ý tốt nhất (dưới ngưỡng / mơ hồ) — chỉ để hiển thị. */
  suggestion?: {
    code: string;
    name: string;
    score: number;
    method: RecipeMatchMethod;
  };
}

export interface RecipeSyncConflict {
  recipeId?: string;
  targetItemId: string;
  type: 'canonical' | 'alias' | 'duplicate_target' | 'missing_id';
  previousItemId: string;
  previousItemName: string;
  nextItemName: string;
  matchBy: RecipeMatchMethod;
  score: number;
  optionAddonsRemapped: number;
}

export interface RecipeSyncResult {
  recipes: Recipe[];
  changed: RecipeSyncChange[];
  unmatched: UnmatchedRecipeRef[];
  conflicts: RecipeSyncConflict[];
}

export interface DishMatchResult {
  dish: MenuDishRef;
  matchBy: RecipeMatchMethod;
  score: number;
}

export const normalizeDishKey = (value?: string): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/** Bỏ tiền tố mã POS / gói size để so khớp. */
export const normalizeCodeKey = (value?: string): string =>
  normalizeDishKey(value)
    .replace(/^tbh[-_]?/, '')
    .replace(/^stpv[-_]?/, '')
    .replace(/^opt[-_]?/, '')
    .replace(/[^a-z0-9]+/g, '');

export const compactDishKey = (value?: string): string =>
  normalizeDishKey(value).replace(/[^a-z0-9]+/g, '');

/** Bỏ từ nhiễu: hat, goi, g, kg, ml… */
export const tokenizeDishName = (value?: string): string[] => {
  const stop = new Set([
    'hat',
    'goi',
    'goi',
    'g',
    'kg',
    'ml',
    'l',
    'ly',
    'chai',
    'phan',
    'the',
    'and',
    'va',
    'cua',
    'cho',
  ]);

  return normalizeDishKey(value)
    .split(/[^a-z0-9]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !stop.has(t) && !/^\d+$/.test(t));
};

export const dishesToMenuRefs = (dishes: Dish[]): MenuDishRef[] =>
  dishes
    .filter((dish) => !!dish.code?.trim() && !!dish.name?.trim())
    .map((dish) => ({
      code: dish.code.trim(),
      name: dish.name.trim(),
      group: (dish.groupName || dish.group || '').trim() || undefined,
    }));

const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
  for (let j = 0; j < cols; j += 1) matrix[0][j] = j;

  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
};

const similarityRatio = (a: string, b: string): number => {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const dist = levenshtein(a, b);
  return 1 - dist / Math.max(a.length, b.length);
};

const tokenJaccard = (left: string[], right: string[]): number => {
  if (!left.length || !right.length) return 0;
  const setL = new Set(left);
  const setR = new Set(right);
  let inter = 0;
  setL.forEach((t) => {
    if (setR.has(t)) inter += 1;
  });
  const union = setL.size + setR.size - inter;
  return union > 0 ? inter / union : 0;
};

const scoreRecipeToDish = (
  recipe: Pick<Recipe, 'itemId' | 'itemName'>,
  dish: MenuDishRef,
): { score: number; method: RecipeMatchMethod } => {
  const recipeCode = (recipe.itemId || '').trim();
  const dishCode = dish.code.trim();
  const recipeName = (recipe.itemName || '').trim();
  const dishName = dish.name.trim();

  if (recipeCode && dishCode && recipeCode === dishCode) {
    return { score: 1000, method: 'code' };
  }

  if (recipeCode && dishCode && recipeCode.toLowerCase() === dishCode.toLowerCase()) {
    return { score: 990, method: 'code_ci' };
  }

  const recipeCodeKey = normalizeCodeKey(recipeCode);
  const dishCodeKey = normalizeCodeKey(dishCode);
  if (recipeCodeKey && dishCodeKey && recipeCodeKey === dishCodeKey) {
    return { score: 960, method: 'code_suffix' };
  }

  // Mã recipe là hậu tố của mã dish hoặc ngược lại (TBH-LAT vs LAT, TBH-BEAN-HR vs BEAN-HR)
  if (
    recipeCodeKey.length >= 3 &&
    dishCodeKey.length >= 3 &&
    (recipeCodeKey.endsWith(dishCodeKey) ||
      dishCodeKey.endsWith(recipeCodeKey) ||
      recipeCodeKey.includes(dishCodeKey) ||
      dishCodeKey.includes(recipeCodeKey))
  ) {
    const gap = Math.abs(recipeCodeKey.length - dishCodeKey.length);
    return { score: Math.max(780, 920 - gap * 15), method: 'code_suffix' };
  }

  const nameRecipe = normalizeDishKey(recipeName);
  const nameDish = normalizeDishKey(dishName);
  if (nameRecipe && nameDish && nameRecipe === nameDish) {
    return { score: 950, method: 'name' };
  }

  const compactRecipe = compactDishKey(recipeName || recipeCode);
  const compactDish = compactDishKey(dishName || dishCode);
  if (compactRecipe && compactDish && compactRecipe === compactDish) {
    return { score: 920, method: 'name_compact' };
  }

  // So tên recipe với mã dish gọn (đôi khi tên CT = mã cũ)
  if (compactRecipe && dishCodeKey && compactRecipe === dishCodeKey) {
    return { score: 880, method: 'code_suffix' };
  }

  if (
    nameRecipe.length >= 4 &&
    nameDish.length >= 4 &&
    (nameRecipe.includes(nameDish) || nameDish.includes(nameRecipe))
  ) {
    const gap = Math.abs(nameRecipe.length - nameDish.length);
    return { score: Math.max(760, 880 - Math.min(gap, 100)), method: 'name_contains' };
  }

  const tokensRecipe = tokenizeDishName(recipeName);
  const tokensDish = tokenizeDishName(dishName);
  const jaccard = tokenJaccard(tokensRecipe, tokensDish);
  if (jaccard >= 0.5) {
    return { score: Math.round(700 + jaccard * 250), method: 'token' };
  }

  // Fuzzy: ratio trên compact + token overlap nhẹ
  const ratioName = similarityRatio(nameRecipe, nameDish);
  const ratioCompact = similarityRatio(compactRecipe, compactDish);
  const bestRatio = Math.max(ratioName, ratioCompact);
  const fuzzyScore = Math.round(bestRatio * 850 + jaccard * 100);

  if (fuzzyScore >= RECIPE_AUTO_MATCH_MIN_SCORE) {
    return { score: fuzzyScore, method: 'fuzzy' };
  }

  if (bestRatio >= 0.55 || jaccard >= 0.35) {
    return { score: Math.max(fuzzyScore, Math.round(bestRatio * 700)), method: 'fuzzy' };
  }

  return { score: 0, method: 'none' };
};

/**
 * Tìm món CUKCUK tốt nhất cho công thức.
 * Tự ghép khi score ≥ ngưỡng và không mơ hồ (top1 − top2 đủ lớn).
 */
export const findDishForRecipe = (
  recipe: Pick<Recipe, 'itemId' | 'itemName'>,
  dishes: MenuDishRef[],
  options?: { minScore?: number; minGap?: number; allowAmbiguous?: boolean },
): DishMatchResult | null => {
  const minScore = options?.minScore ?? RECIPE_AUTO_MATCH_MIN_SCORE;
  const minGap = options?.minGap ?? RECIPE_AUTO_MATCH_MIN_GAP;

  if (!dishes.length) return null;

  const ranked = dishes
    .map((dish) => {
      const { score, method } = scoreRecipeToDish(recipe, dish);
      return { dish, score, matchBy: method };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.dish.code.localeCompare(b.dish.code));

  if (!ranked.length) return null;

  const top = ranked[0];
  const second = ranked[1];
  const gap = second ? top.score - second.score : 999;

  if (top.score < minScore) return null;
  if (!options?.allowAmbiguous && second && gap < minGap && second.score >= minScore - 50) {
    return null;
  }

  return { dish: top.dish, matchBy: top.matchBy, score: top.score };
};

/** Best candidate kể cả dưới ngưỡng — để hiện gợi ý. */
export const suggestDishForRecipe = (
  recipe: Pick<Recipe, 'itemId' | 'itemName'>,
  dishes: MenuDishRef[],
): DishMatchResult | null => {
  let best: DishMatchResult | null = null;

  dishes.forEach((dish) => {
    const { score, method } = scoreRecipeToDish(recipe, dish);
    if (score <= 0) return;
    if (!best || score > best.score) {
      best = { dish, matchBy: method, score };
    }
  });

  return best;
};

/** Catalog option theo món: optionName chuẩn hóa → option code. */
export const buildOptionCodeByNameForDish = (
  customizations: Customization[],
  dishCode: string,
): Map<string, string> => {
  const map = new Map<string, string>();
  const target = dishCode.trim().toLowerCase();

  customizations.forEach((customization) => {
    const codes = Array.isArray(customization.dishCode)
      ? customization.dishCode
      : customization.dishCode
        ? [String(customization.dishCode)]
        : [];
    const applies =
      !target ||
      codes.some((code) => String(code).trim().toLowerCase() === target);

    if (!applies && codes.length > 0) return;

    (customization.groups || []).forEach((group) => {
      (group.dishCodes || []).forEach((opt) => {
        const optionName = opt.name?.trim();
        const optionCode = opt.code?.trim();
        if (!optionName || !optionCode) return;
        const key = normalizeDishKey(optionName);
        if (key && !map.has(key)) {
          map.set(key, optionCode);
        }
        const compactKey = compactDishKey(optionName);
        if (compactKey && !map.has(compactKey)) {
          map.set(compactKey, optionCode);
        }
      });
    });
  });

  return map;
};

export const remapOptionAddons = (
  addons: OptionAddon[] | undefined,
  optionCodeByName: Map<string, string>,
  allOptionCatalog?: Array<{ code: string; name: string }>,
): { addons: OptionAddon[]; remapped: number } => {
  if (!addons?.length) return { addons: addons || [], remapped: 0 };

  let remapped = 0;
  const next = addons.map((addon) => {
    const byName =
      optionCodeByName.get(normalizeDishKey(addon.optionName)) ||
      optionCodeByName.get(compactDishKey(addon.optionName));

    if (byName && byName !== addon.optionId) {
      remapped += 1;
      return { ...addon, optionId: byName };
    }

    // Fuzzy option trong catalog toàn menu
    if (allOptionCatalog?.length) {
      const target = compactDishKey(addon.optionName);
      const targetNorm = normalizeDishKey(addon.optionName);

      let bestCode = '';
      let bestScore = 0;

      for (const opt of allOptionCatalog) {
        const compact = compactDishKey(opt.name);
        const norm = normalizeDishKey(opt.name);
        let score = 0;
        if (compact && target && compact === target) score = 950;
        else if (norm && targetNorm && norm === targetNorm) score = 920;
        else if (norm && targetNorm && (norm.includes(targetNorm) || targetNorm.includes(norm))) {
          score = 780;
        } else {
          score = Math.round(similarityRatio(compact || norm, target || targetNorm) * 900);
        }
        if (score > bestScore) {
          bestScore = score;
          bestCode = opt.code;
        }
      }

      if (bestCode && bestScore >= RECIPE_AUTO_MATCH_MIN_SCORE && bestCode !== addon.optionId) {
        remapped += 1;
        return { ...addon, optionId: bestCode };
      }
    }

    return addon;
  });

  return { addons: next, remapped };
};

const collectAllOptions = (
  customizations: Customization[],
): Array<{ code: string; name: string }> => {
  const list: Array<{ code: string; name: string }> = [];
  const seen = new Set<string>();

  customizations.forEach((customization) => {
    (customization.groups || []).forEach((group) => {
      (group.dishCodes || []).forEach((opt) => {
        const code = opt.code?.trim();
        const name = opt.name?.trim();
        if (!code || !name || seen.has(code)) return;
        seen.add(code);
        list.push({ code, name });
      });
    });
  });

  return list;
};

/**
 * Tự gán công thức → món CUKCUK (mã / tên / fuzzy).
 * Chỉ ghép khi đủ điểm và không mơ hồ.
 */
export const syncRecipesToCukcukMenu = (
  recipes: Recipe[],
  dishes: MenuDishRef[],
  customizations: Customization[] = [],
  options?: { minScore?: number; minGap?: number },
): RecipeSyncResult => {
  const unmatched: UnmatchedRecipeRef[] = [];
  const allOptions = collectAllOptions(customizations);
  const matches = recipes.map((recipe) => findDishForRecipe(recipe, dishes, options));
  const proposedChanges: Array<RecipeSyncChange | null> = [];

  const proposed = recipes.map((recipe, index) => {
    const match = matches[index];
    if (!match) {
      const suggestion = suggestDishForRecipe(recipe, dishes);
      unmatched.push({
        id: recipe.id,
        itemId: recipe.itemId,
        itemName: recipe.itemName,
        group: recipe.group,
        ingredientCount: recipe.ingredients?.length || 0,
        suggestion: suggestion ? {
          code: suggestion.dish.code,
          name: suggestion.dish.name,
          score: suggestion.score,
          method: suggestion.matchBy,
        } : undefined,
      });
      proposedChanges.push(null);
      return recipe;
    }

    const { addons, remapped } = remapOptionAddons(
      recipe.optionAddons,
      buildOptionCodeByNameForDish(customizations, match.dish.code),
      allOptions,
    );
    const itemIdChanged = normalizeDishKey(match.dish.code) !== normalizeDishKey(recipe.itemId);
    if (!itemIdChanged && match.dish.name === recipe.itemName && remapped === 0) {
      proposedChanges.push(null);
      return recipe;
    }

    proposedChanges.push({
      recipeId: recipe.id,
      previousItemId: recipe.itemId,
      previousItemName: recipe.itemName,
      nextItemId: match.dish.code,
      nextItemName: match.dish.name,
      matchBy: match.matchBy,
      score: match.score,
      optionAddonsRemapped: remapped,
    });
    const aliases = itemIdChanged
      ? [...(recipe.itemIdAliases || []), recipe.itemId]
          .map((alias) => alias.trim())
          .filter((alias) => alias && normalizeCodeKey(alias) !== normalizeCodeKey(match.dish.code))
      : recipe.itemIdAliases;

    return {
      ...recipe,
      itemId: match.dish.code,
      itemIdAliases: aliases ? Array.from(new Map(aliases.map((alias) => [normalizeCodeKey(alias), alias])).values()) : aliases,
      itemName: match.dish.name,
      optionAddons: addons,
    };
  });

  const owners = new Map<string, number[]>();
  proposed.forEach((recipe, index) => {
    [recipe.itemId, ...(recipe.itemIdAliases || [])].forEach((value) => {
      const key = normalizeCodeKey(value);
      if (!key) return;
      const indexes = owners.get(key) || [];
      if (!indexes.includes(index)) indexes.push(index);
      owners.set(key, indexes);
    });
  });
  const invalid = new Set<number>();
  owners.forEach((indexes) => {
    if (indexes.length > 1) indexes.forEach((index) => invalid.add(index));
  });
  proposedChanges.forEach((change, index) => {
    if (change && !recipes[index].id) invalid.add(index);
  });

  const conflicts: RecipeSyncConflict[] = [];
  invalid.forEach((index) => {
    const change = proposedChanges[index];
    if (!change) return;
    const recipe = recipes[index];
    const targetKey = normalizeCodeKey(change.nextItemId);
    let type: RecipeSyncConflict['type'] = !recipe.id ? 'missing_id' : 'duplicate_target';
    const targetOwners = owners.get(targetKey) || [];
    const allOwnersProposedTarget = targetOwners.length > 1 && targetOwners.every((ownerIndex) =>
      proposedChanges[ownerIndex]?.nextItemId && normalizeCodeKey(proposedChanges[ownerIndex]!.nextItemId) === targetKey,
    );
    const otherIndex = proposed.findIndex((other, i) => i !== index && [other.itemId, ...(other.itemIdAliases || [])]
      .some((value) => normalizeCodeKey(value) === targetKey));
    if (!allOwnersProposedTarget && otherIndex >= 0) {
      type = normalizeCodeKey(proposed[otherIndex].itemId) === targetKey ? 'canonical' : 'alias';
    }
    conflicts.push({ ...change, targetItemId: change.nextItemId, type });
  });

  return {
    recipes: proposed.map((recipe, index) => invalid.has(index) ? recipes[index] : recipe),
    changed: proposedChanges.filter((change, index): change is RecipeSyncChange => !!change && !invalid.has(index)),
    unmatched,
    conflicts,
  };
};

export const recipesNeedingPersist = (
  original: Recipe[],
  synced: Recipe[],
): Recipe[] => {
  const originalById = new Map(original.map((recipe) => [recipe.id || recipe.itemId, recipe]));

  return synced.filter((recipe) => {
    const key = recipe.id || recipe.itemId;
    const prev = originalById.get(key);
    if (!prev) return true;
    if (prev.itemId !== recipe.itemId || prev.itemName !== recipe.itemName) return true;
    if (JSON.stringify(prev.itemIdAliases || []) !== JSON.stringify(recipe.itemIdAliases || [])) {
      return true;
    }

    const prevAddons = JSON.stringify(prev.optionAddons || []);
    const nextAddons = JSON.stringify(recipe.optionAddons || []);
    return prevAddons !== nextAddons;
  });
};
